import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { consola } from "consola";
import type { CarrouselBanner } from '@/types/banners';

export const useBannersStore = defineStore('banners', () => {
  const banners = ref<CarrouselBanner[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const currentIndex = ref(0);

  const config = useRuntimeConfig();
  const CFIMAGE_URL = config.public.CLOUDFLARE_HOST_URL;

  const processedBanners = computed(() => {
    if (!banners.value?.length) return [];
    return banners.value
      .map((banner) => ({
        ...banner,
        image_mobile: banner.image_mobile ?? banner.image,
      }))
      .filter((banner) => banner.image_mobile || banner.image);
  });

  function getUrlImageValid(image: string, display: string) {
    const cleanedCodeImage = image.replace(/-/g, '');
    const isCloudflareCode = cleanedCodeImage.length === 32;
    return isCloudflareCode ? `${CFIMAGE_URL}${image}/${display}` : image;
  }

  function getImageUrl(banner: CarrouselBanner, isMobile: boolean) {
    const display = isMobile ? 'banMobile' : 'banDesktop';
    if (isMobile) {
      return getUrlImageValid(banner.image_mobile || banner.image, display);
    }
    return getUrlImageValid(banner.image, display);
  }

  function setCurrentIndex(index: number) {
    currentIndex.value = index;
  }

  async function fetchBanners() {
    isLoading.value = true;

    try {
      const { data } = await useFetch<CarrouselBanner[]>('/v2/banners', { retry: false });
      banners.value = data.value ?? [];
    } catch (error) {
      consola.error("Failed to fetch banners", error);
    }

    isLoading.value = false;
  }

  function handleBannerClick(url: string, event: Event) {
    const target = event.target as HTMLElement;
    if (target.closest('button')) {
      event.preventDefault();
      return;
    }

    if (!url) return;
    const urlObj = new URL(url, window.location.origin);
    const code = urlObj.searchParams.get('code');
    const login = urlObj.searchParams.get('login');

    if (code) {
      useCookie('code', {
        sameSite: 'none',
        secure: true,
        maxAge: 1000 * 60 * 60 * 24 * 7,
      }).value = code;
    }

    if (login === '1') {
      useAuthStore().modalOpen('login');
      return;
    } else if (login === '0') {
      useAuthStore().modalOpen('register');
      return;
    }
  }

  return {
    banners,
    isLoading,
    error,
    processedBanners,
    currentIndex,
    getImageUrl,
    fetchBanners,
    handleBannerClick,
    setCurrentIndex,
  };
});